import {
  ColDef,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  Module,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import {
  HeliosColumnTypes,
  TextFilterOptionsWithContainsIn,
  UpdateStatus,
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  heliosClientSideRowConfig,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import {
  ExternalTransaction,
  getUniqueRowId,
  getUniqueRowIdFromExtTran,
} from '@helios/common-api'
import {
  BulkTransactionModalMode,
  BulkTransactionModalResponse,
  BulkTransactionsModalService,
} from '@helios/create-transactions-modal'
import {
  TransactionsStatusBarModule,
  transactionsStatusBarPanel,
} from '@helios/generic-components'
import { handleColumnVisibilityChange } from '@helios/server-side-util'
import { RootState, selectUserPolicy } from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { NfColumnTypes } from '@northfork/ag-grid'
import { Observable, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { BulkExternalTransactionMainGridKey } from '../../../panel-key'
import { BulkExternalTransaction } from '../../models/bulk-external-transactions.model'
import { externalTransactionField as field } from '../../models/external-transaction'

export interface TxToUpdateStatus {
  uniqueTranIds: string[]
  requestStatus: UpdateStatus
}

@Component({
  selector: 'hs-bulk-external-transactions-grid',
  templateUrl: './bulk-external-transactions-grid.component.html',
  styleUrls: ['./bulk-external-transactions-grid.component.scss'],
})
export class BulkExternalTransactionsGridComponent
  implements OnInit, OnDestroy
{
  @ViewChild('searchPopoverComponent') searchPopoverComponent?: NgbPopover

  gridModules: Module[] = [RangeSelectionModule, TransactionsStatusBarModule]
  gridOptions = this.createGridOptions()
  gridApi?: GridApi
  destroySubject$ = new Subject<void>()
  @Output() cancel = new EventEmitter()

  @Input()
  set updateStatuses(updates: TxToUpdateStatus | null) {
    if (updates == null) return
    if (this.gridApi == null) return
    const updatedRows = []
    for (const uniqueTranId of updates.uniqueTranIds) {
      const node = this.gridApi.getRowNode(uniqueTranId)
      if (node == null) continue
      const updatedData = {
        ...node.data,
        [field('isEdited')]: updates.requestStatus,
      }
      updatedRows.push(updatedData)
    }
    const transaction = { update: updatedRows }
    this.gridApi.applyTransaction(transaction)
  }
  @Input() set isSearchPopoverShown(isShown: boolean) {
    if (isShown) {
      this.searchPopoverComponent?.open()
    } else {
      this.searchPopoverComponent?.close()
    }
  }

  @Input() data: ExternalTransaction[] = []
  @Input() isLoading = false
  @Input() transactionsLimitExceeded = false

  @Output() isSearchPopoverShownChange = new EventEmitter<boolean>()
  @Output() clearTable = new EventEmitter<void>()

  @Output() ignoreSelected = new EventEmitter<ExternalTransaction[]>()
  @Output() unignoredSelected = new EventEmitter<ExternalTransaction[]>()
  @Output() holdSelected = new EventEmitter<ExternalTransaction[]>()
  @Output() unholdSelected = new EventEmitter<ExternalTransaction[]>()

  hasBulkToolActionsAccess = false
  readonly bulkToolActions$: Observable<boolean> = this.store
    .select(selectUserPolicy)
    .pipe(map(userPolicy => userPolicy?.bulkToolActions ?? false))

  cancelRequestTitle = 'Cancel Find Transactions request'

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly bulkTransactionsModalService: BulkTransactionsModalService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly store: Store<RootState>,
  ) {}

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.destroy()
  }

  ngOnInit() {
    this.bulkToolActions$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(permission => (this.hasBulkToolActionsAccess = permission))
  }

  onSearchPopoverShown() {
    this.isSearchPopoverShownChange.emit(true)
  }

  onSearchPopoverHidden() {
    this.isSearchPopoverShownChange.emit(false)
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosMultiRowSelectionConfig,
      ...heliosClientSideRowConfig,
      allowContextMenuWithControlKey: true,
      columnDefs: this.getColDefs(),
      getContextMenuItems: params => this.getContextMenuItems(params),
      statusBar: transactionsStatusBarPanel,
      defaultColDef: {
        ...heliosClientSideRowConfig.defaultColDef,
        filterParams: {},
      },
      getRowNodeId: (tx: BulkExternalTransaction) =>
        getUniqueRowIdFromExtTran(tx),
      onGridReady: event => this.onGridReady(event),
      onColumnVisible: params =>
        handleColumnVisibilityChange(params, 'ClearwaterTransactions'),
      rowClassRules: {
        'font-italic': params => {
          return params.data?.tranSource === 'Synthetic'
        },
      },
      ...disableChangeDetectionOnScroll(this.changeDetector),
    }
  }

  /**
   * Right-click menu
   *
   *
   * @internal
   */
  getContextMenuItems(params: GetContextMenuItemsParams) {
    const menuItems: ('separator' | MenuItemDef)[] = []

    let rowsSelected: BulkExternalTransaction[]
    if (!params?.node?.id) {
      return menuItems
    }

    if (params.api.getSelectedNodes().length > 1) {
      rowsSelected = params.api.getSelectedNodes().map(t => t.data) ?? []
    } else {
      rowsSelected = [params.node.data] as ExternalTransaction[]
    }
    const suffix =
      rowsSelected.length > 1 ? ' (' + rowsSelected.length + ')' : ''
    menuItems.push(
      {
        name: 'Post Cash Entry' + suffix,
        disabled: !this.hasBulkToolActionsAccess,
        action: () => {
          const response =
            this.bulkTransactionsModalService.toggleCashEntryModal({
              modalMode: BulkTransactionModalMode.CreateFromExtTran,
              externalTransaction: rowsSelected,
            })
          this.handlePostModalResponse(rowsSelected, response)
        },
      },
      {
        name: 'Post Trade' + suffix,
        disabled: !this.hasBulkToolActionsAccess,
        action: () => {
          const response =
            this.bulkTransactionsModalService.toggleTradeEntryModal({
              modalMode: BulkTransactionModalMode.CreateFromExtTran,
              externalTransaction: rowsSelected,
            })
          this.handlePostModalResponse(rowsSelected, response)
        },
      },
      {
        name: 'Post FX Trade' + suffix,
        disabled: !this.hasBulkToolActionsAccess,
        action: () => {
          const response =
            this.bulkTransactionsModalService.toggleFxSpotTradeModal({
              modalMode: BulkTransactionModalMode.CreateFromExtTran,
              externalTransaction: rowsSelected,
            })
          this.handlePostModalResponse(rowsSelected, response)
        },
      },
      {
        name: 'Post Currency Forward' + suffix,
        disabled: !this.hasBulkToolActionsAccess,
        action: () => {
          const response =
            this.bulkTransactionsModalService.toggleFxForwardModal({
              modalMode: BulkTransactionModalMode.CreateFromExtTran,
              externalTransaction: rowsSelected,
            })
          this.handlePostModalResponse(rowsSelected, response)
        },
      },
      {
        name: 'Post Wire Transfer' + suffix,
        disabled: !this.hasBulkToolActionsAccess,
        action: () => {
          const response =
            this.bulkTransactionsModalService.toggleWireTransferModal({
              modalMode: BulkTransactionModalMode.CreateFromExtTran,
              externalTransaction: rowsSelected,
            })
          this.handlePostModalResponse(rowsSelected, response)
        },
      },
      {
        name: 'Post Security Transfer In' + suffix,
        disabled: !this.hasBulkToolActionsAccess,
        action: () => {
          const response =
            this.bulkTransactionsModalService.toggleSecurityTransferInModal({
              modalMode: BulkTransactionModalMode.CreateFromExtTran,
              externalTransaction: rowsSelected,
            })
          this.handlePostModalResponse(rowsSelected, response)
        },
      },
      {
        name: 'Post Security Transfer Out' + suffix,
        disabled: !this.hasBulkToolActionsAccess,
        action: () => {
          const response =
            this.bulkTransactionsModalService.toggleSecurityTransferOutModal({
              modalMode: BulkTransactionModalMode.CreateFromExtTran,
              externalTransaction: rowsSelected,
            })
          this.handlePostModalResponse(rowsSelected, response)
        },
      },
    )

    menuItems.push('separator')

    /**
     * Logic for disabling menu items:
     *  https://bitbucket.org/clearwateranalytics/helios/src/acc275ce600c1065bb21fd363bb3e2152598f489/services/bulk-ext-transactions/src/main/kotlin/com/clearwateranalytics/helios/bulkexttransactions/BulkExtTransService.kt#lines-221
     */
    menuItems.push(
      {
        name: 'Ignore' + suffix,
        disabled:
          !rowsSelected.every(row => row.availableActions.includes('ignore')) ||
          !this.hasBulkToolActionsAccess,
        action: () => this.ignoreSelected.next(rowsSelected),
      },
      {
        name: 'Unignore' + suffix,
        disabled:
          !rowsSelected.every(row =>
            row.availableActions.includes('unignore'),
          ) || !this.hasBulkToolActionsAccess,
        action: () => this.unignoredSelected.next(rowsSelected),
      },
      {
        name: 'Hold' + suffix,
        disabled:
          !rowsSelected.every(row => row.availableActions.includes('hold')) ||
          !this.hasBulkToolActionsAccess,
        action: () => this.holdSelected.next(rowsSelected),
      },
      {
        name: 'Unhold' + suffix,
        disabled:
          !rowsSelected.every(row => row.availableActions.includes('unhold')) ||
          !this.hasBulkToolActionsAccess,
        action: () => this.unholdSelected.next(rowsSelected),
      },
    )

    return menuItems
  }

  async onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api

    this.savedViewService.manageSavedView(
      event,
      BulkExternalTransactionMainGridKey,
      this.destroySubject$,
    )

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    enableHeliosMultiRowDragSelection(event.api)
    enableHeliosCopyBehavior(event.api)
  }

  clearFilters() {
    this.clearTable.emit()
  }

  onCancelRequest() {
    this.cancel.next()
  }

  private getColDefs(): ColDef[] {
    return [
      {
        field: field('isEdited'),
        type: HeliosColumnTypes.BulkIsEdited,
        sortable: true,
      },
      {
        filter: 'agSetColumnFilter',
        headerName: 'Status',
        field: field('status'),
      },
      {
        headerName: 'Asset ID',
        field: field('secIdentifier'),
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: field('secCusip'),
        hide: true,
      },
      {
        field: field('secIsin'),
        sortable: true,
        hide: true,
      },
      {
        field: field('secFigi'),
        sortable: true,
        hide: true,
      },
      {
        field: field('secSedol'),
        sortable: true,
        hide: true,
      },
      {
        filter: 'agSetColumnFilter',
        headerName: 'Tran Type Code',
        field: field('tranTypeCode'),
        sortable: true,
      },
      {
        headerName: 'Trade Date',
        field: field('tradeDate'),
        type: NfColumnTypes.Date,
        sortable: true,
      },
      {
        headerName: 'Settle Date',
        field: field('settleDate'),
        type: NfColumnTypes.Date,
        sortable: true,
      },
      {
        headerName: 'Post Date',
        field: field('postDate'),
        type: NfColumnTypes.Date,
        sortable: true,
      },
      {
        headerName: 'Amount',
        field: field('amount'),
        valueFormatter: params =>
          params.data.isHlpAccount
            ? this.formatNumber(params, 4)
            : this.formatNumber(params, 2),
        sortable: true,
      },
      {
        headerName: 'Price',
        field: field('price'),
        type: NfColumnTypes.Number,
        sortable: true,
      },
      {
        headerName: 'Units',
        field: field('units'),
        valueFormatter: params =>
          params.data.isHlpAccount
            ? this.formatNumber(params, 4)
            : this.formatNumber(params, 2),
        sortable: true,
      },
      {
        headerName: 'Description',
        field: field('description'),
      },
      {
        headerName: 'Execution Type',
        field: field('executionType'),
      },
      {
        filter: 'agSetColumnFilter',
        headerName: 'Sec Type Code',
        field: field('securityTypeCode'),
        sortable: true,
      },
      {
        headerName: 'Accrued',
        field: field('accrued'),
        type: NfColumnTypes.Money,
        hide: true,
      },
      {
        headerName: 'Commission',
        field: field('commission'),
        type: NfColumnTypes.Money,
        hide: true,
      },
      {
        headerName: 'Origination Fee',
        field: field('originationFee'),
        hide: true,
      },
      {
        headerName: 'Loan Prepaid Interest',
        field: field('prepaidInterest'),
        hide: true,
      },
      {
        headerName: 'Loan Past Due Interest',
        field: field('interestDue'),
        hide: true,
      },
      {
        headerName: 'Fee',
        field: field('fee'),
        type: NfColumnTypes.Money,
        hide: true,
      },
      {
        filter: 'agSetColumnFilter',
        headerName: 'Currency Code',
        field: field('currencyCode'),
        hide: true,
      },
      {
        headerName: 'Ext Asset ID',
        field: field('externalSecIdentifier'),
        hide: true,
      },
      {
        field: field('externalCusip'),
        hide: true,
      },
      {
        field: field('externalSecAltIdentifier'),
        headerName: 'Ext Alternate ID',
        hide: true,
      },
      {
        headerName: 'Aggregate info',
        field: field('aggInfoString'),
        hide: true,
      },
      {
        field: field('extTranId'),
        headerName: 'Ext Transaction ID',
        type: NfColumnTypes.Identifier,
      },
      {
        field: field('transactionBasis'),
        headerName: 'Transaction Basis',
      },
      {
        field: field('executionId'),
        headerName: 'Execution ID',
        type: NfColumnTypes.Identifier,
      },
      {
        filter: 'agSetColumnFilter',
        headerName: 'Data Source Type',
        field: field('dataSourceType'),
      },
      {
        headerName: 'Security ID',
        field: field('securityId'),
        hide: true,
      },
      {
        headerName: 'Recon Date',
        field: field('reconDate'),
        type: NfColumnTypes.Date,
        hide: true,
      },
      {
        headerName: 'Status Changed By',
        field: field('statusChangedBy'),
        hide: true,
      },
      {
        headerName: 'Sell Units',
        field: field('fromUnits'),
        valueFormatter: params =>
          params.data.isHlpAccount
            ? this.formatNumber(params, 4)
            : this.formatNumber(params, 2),
        hide: true,
      },
      {
        field: field('fromAmount'),
        valueFormatter: params =>
          params.data.isHlpAccount
            ? this.formatNumber(params, 4)
            : this.formatNumber(params, 2),
        hide: true,
      },
      {
        headerName: 'FX Rate',
        field: field('exchangeRate'),
        type: NfColumnTypes.Number,
        hide: true,
      },
      {
        headerName: 'Type Label',
        field: field('typeLabel'),
        hide: true,
      },
      {
        headerName: 'Account ID',
        field: field('accountId'),
        hide: true,
        type: NfColumnTypes.Identifier,
      },
      {
        headerName: 'Contract ID',
        field: field('contractIdentifier'),
        hide: true,
      },
      {
        headerName: 'Account Name',
        field: field('accountName'),
        hide: true,
      },
      {
        headerName: 'Data Source',
        field: field('dataSource'),
      },
      {
        field: field('externalSecDesc'),
        hide: true,
      },
      {
        field: field('externalSecAltIdDesc'),
        headerName: 'Ext Alternate ID Description',
        hide: true,
      },
      {
        headerName: 'CW Lot ID',
        field: field('cwLotId'),
        hide: true,
      },
      {
        headerName: 'Capitalized',
        field: field('isCapitalize'),
        hide: true,
      },
    ]
  }

  /**
   * @internal
   *
   * @param rowsSelected
   * @param response
   * @private
   */
  async handlePostModalResponse(
    rowsSelected: BulkExternalTransaction[],
    response: Promise<BulkTransactionModalResponse>,
  ) {
    const { success, transactionRequests } = await response

    const affectedIds = transactionRequests.map(req =>
      getUniqueRowId(
        req.metaData.externalTranId as number,
        req.metaData.externalAccountId as number,
      ),
    )

    this.updateStatuses = {
      uniqueTranIds: affectedIds,
      requestStatus: success ? 'SUCCESS' : 'FAILURE',
    }
  }

  formatNumber(params: ValueFormatterParams, precision: number): string {
    return params.value
      ? Number(params.value).toLocaleString(undefined, {
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        })
      : params.value
  }
}
